import { For, Show, createSignal } from "solid-js";

import { entityDetailLink } from "~/components/entityDetailLink";
import { useAuditLog } from "~/lib/hooks";
import type { AuditLogRead } from "~/lib/types";

type Props = {
  wsKey: () => string;
};

const ENTITY_TYPE_OPTIONS = [
  "",
  "account",
  "contact",
  "deal",
  "product",
  "activity",
  "note",
  "custom_field_definition",
  "custom_object_definition",
  "custom_record",
  "deal_line_item",
  "subject_erasure",
];

const ACTION_OPTIONS = ["", "create", "update", "archive", "unarchive", "merge", "delete"];

/**
 * Read-only audit log viewer. Admin-only — surface the rows that the
 * service layer writes on every state change. Retention is 60 days
 * (ops/retention.py) so rows older than that won't show even when no
 * filter is applied.
 */
export function AuditLogPanel(props: Props) {
  const [entityType, setEntityType] = createSignal<string>("");
  const [action, setAction] = createSignal<string>("");
  const [actorId, setActorId] = createSignal<string>("");

  const audit = useAuditLog(props.wsKey, () => ({
    entity_type: entityType() || undefined,
    action: action() || undefined,
    actor_id: actorId() || undefined,
    limit: 50,
  }));

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 class="card-title text-base">Audit log</h3>
            <p class="text-xs opacity-60 mt-0.5">
              Every state change. Retained 60 days. Latest 50 shown — narrow with filters.
            </p>
          </div>
        </div>

        <div class="flex items-center gap-2 flex-wrap mt-3">
          <label class="text-sm opacity-70" for="al-entity">
            Entity:
          </label>
          <select
            id="al-entity"
            class="select select-bordered select-sm"
            value={entityType()}
            onChange={(e) => setEntityType(e.currentTarget.value)}
          >
            <For each={ENTITY_TYPE_OPTIONS}>
              {(et) => <option value={et}>{et || "all"}</option>}
            </For>
          </select>
          <label class="text-sm opacity-70 ml-2" for="al-action">
            Action:
          </label>
          <select
            id="al-action"
            class="select select-bordered select-sm"
            value={action()}
            onChange={(e) => setAction(e.currentTarget.value)}
          >
            <For each={ACTION_OPTIONS}>{(a) => <option value={a}>{a || "all"}</option>}</For>
          </select>
          <input
            type="text"
            class="input input-bordered input-sm w-48"
            placeholder="Actor id…"
            value={actorId()}
            onInput={(e) => setActorId(e.currentTarget.value.trim())}
            aria-label="Filter by actor id"
          />
        </div>

        <Show when={audit.isLoading}>
          <div role="status" aria-live="polite" class="text-center py-6 opacity-60">
            Loading…
          </div>
        </Show>

        <div class="overflow-x-auto mt-2">
          <table class="table table-xs">
            <thead>
              <tr>
                <th>When</th>
                <th>Actor</th>
                <th>Action</th>
                <th>Entity</th>
                <th>Entity id</th>
                <th />
              </tr>
            </thead>
            <tbody>
              <For each={audit.data?.items ?? []}>
                {(row) => <Row row={row} wsKey={props.wsKey} />}
              </For>
              <Show when={(audit.data?.items.length ?? 0) === 0 && !audit.isLoading}>
                <tr>
                  <td colspan={6} class="text-center py-6 opacity-60 text-sm">
                    No audit rows match.
                  </td>
                </tr>
              </Show>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function Row(props: { row: AuditLogRead; wsKey: () => string }) {
  const [expanded, setExpanded] = createSignal(false);
  // Either diff data OR metadata makes the row interesting enough to
  // surface the expander. ScaiControl-initiated actions have no diff
  // (the response lands later via webhook) but they do have metadata.
  const hasDetail = () =>
    props.row.before !== null ||
    props.row.after !== null ||
    (props.row.metadata !== null &&
      Object.keys(props.row.metadata ?? {}).length > 0);
  // CRM-initiated ScaiControl writes stamp a request_id into metadata;
  // surface it inline as a copy-able support reference so admins can
  // paste it into tickets the same way the change-plan dialog does.
  const scaicontrolRef = () => {
    const m = props.row.metadata;
    if (!m) return null;
    const code = typeof m.scaicontrol_code === "string" ? m.scaicontrol_code : null;
    const reqId =
      typeof m.scaicontrol_request_id === "string"
        ? m.scaicontrol_request_id
        : null;
    if (!code && !reqId) return null;
    return { code, reqId };
  };
  const entityCell = () => {
    const label = `${props.row.entity_id.slice(0, 14)}…`;
    const link = entityDetailLink(
      props.wsKey(),
      props.row.entity_type,
      props.row.entity_id,
      label,
      props.row.object_key,
    );
    return link ?? <code class="text-xs opacity-60">{label}</code>;
  };
  return (
    <>
      <tr>
        <td class="text-xs opacity-70 whitespace-nowrap">
          {new Date(props.row.created_at).toLocaleString()}
        </td>
        <td class="text-xs">
          <span class="badge badge-ghost badge-xs mr-1">{props.row.actor_kind}</span>
          <code class="text-xs">{props.row.actor_id.slice(0, 14)}…</code>
        </td>
        <td>
          <span
            class={`badge badge-sm ${
              props.row.action === "create"
                ? "badge-success"
                : props.row.action === "archive" || props.row.action === "delete"
                  ? "badge-error"
                  : "badge-outline"
            }`}
          >
            {props.row.action}
          </span>
        </td>
        <td class="text-xs">{props.row.entity_type}</td>
        <td class="text-xs">
          {entityCell()}
          <Show when={scaicontrolRef()}>
            <div class="mt-0.5 text-xs opacity-70">
              <span class="opacity-60">ScaiControl ref:</span>{" "}
              <Show when={scaicontrolRef()!.code}>
                <code>{scaicontrolRef()!.code}</code>
              </Show>
              <Show when={scaicontrolRef()!.code && scaicontrolRef()!.reqId}>
                <span class="opacity-60"> · </span>
              </Show>
              <Show when={scaicontrolRef()!.reqId}>
                <code>{scaicontrolRef()!.reqId}</code>
              </Show>
            </div>
          </Show>
        </td>
        <td>
          <Show when={hasDetail()}>
            <button
              type="button"
              class="btn btn-ghost btn-xs"
              onClick={() => setExpanded((e) => !e)}
              aria-expanded={expanded()}
            >
              {expanded() ? "Hide" : "Diff"}
            </button>
          </Show>
        </td>
      </tr>
      <Show when={expanded()}>
        <tr>
          <td colspan={6} class="bg-base-200">
            <div class="grid grid-cols-1 md:grid-cols-2 gap-2 p-2">
              <Show when={props.row.before}>
                <div>
                  <div class="text-xs uppercase opacity-50 mb-1">Before</div>
                  <pre class="text-xs bg-base-100 rounded-md p-2 overflow-x-auto max-h-64">
                    {JSON.stringify(props.row.before, null, 2)}
                  </pre>
                </div>
              </Show>
              <Show when={props.row.after}>
                <div>
                  <div class="text-xs uppercase opacity-50 mb-1">After</div>
                  <pre class="text-xs bg-base-100 rounded-md p-2 overflow-x-auto max-h-64">
                    {JSON.stringify(props.row.after, null, 2)}
                  </pre>
                </div>
              </Show>
              <Show
                when={
                  props.row.metadata &&
                  Object.keys(props.row.metadata).length > 0
                }
              >
                <div class="md:col-span-2">
                  <div class="text-xs uppercase opacity-50 mb-1">Metadata</div>
                  <pre class="text-xs bg-base-100 rounded-md p-2 overflow-x-auto max-h-64">
                    {JSON.stringify(props.row.metadata, null, 2)}
                  </pre>
                </div>
              </Show>
            </div>
          </td>
        </tr>
      </Show>
    </>
  );
}
